import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getLocalTimeZone, parseDate, parseTime, today } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import { DatePicker } from '@heroui/date-picker';
import { addToast } from '@heroui/toast';
import { hasAspectRatio, BANNER_RATIO_ERROR } from '@/utils/imageRatio';
import { TimeInput } from '@heroui/date-input';
import { Input, Textarea } from '@heroui/input';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { Switch } from '@heroui/switch';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker } from '@react-google-maps/api';
import { IconChevronLeft as ChevronLeft } from '@tabler/icons-react';
import { useOrganization } from '@/contexts/organization-context';
import { createEventWithImages, updateEvent, checkHandleAvailability, fetchDraftById } from '@/apiEndpoints';
import VenuePicker from '@/components/venue-picker/VenuePicker';
import { CURRENCY_OPTIONS, resolveCurrency, type EventCurrency } from '@/utils/currency';
import { btnAccent, card } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
const textareaCls = { inputWrapper: `py-3 ${fieldWrap}`, input: fieldText };
const selectCls = { trigger: fieldWrap, value: `${fieldText} dark:text-nightTextSecondary` };
const dateCls = { inputWrapper: `h-14 min-h-14 ${fieldWrap}` };
const fieldLabel = 'mb-2 block text-sm font-semibold text-grey20 dark:text-nightTextSecondary';
const helpText = 'mt-2 text-xs leading-relaxed text-grey40 dark:text-nightMuted';
const toggleRow = 'flex items-center justify-between gap-4 rounded-2xl bg-grey99 px-4 py-3.5 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
const toggleTitle = 'mb-0.5 text-sm font-semibold text-grey10 dark:text-white';
const toggleDesc = 'text-xs leading-relaxed text-grey40 dark:text-nightMuted';
const switchCls = { wrapper: 'group-data-[selected=true]:bg-primary dark:group-data-[selected=true]:bg-nightAccent' };
const skel = 'bg-grey95 dark:bg-white/10';

const CATEGORY_OPTIONS = [
    { value: 'networking', label: 'Networking' },
    { value: 'business', label: 'Business' },
    { value: 'music', label: 'Music' },
    { value: 'art', label: 'Arts & Culture' },
    { value: 'social', label: 'Social & Community' },
    { value: 'health', label: 'Health & Wellness' },
    { value: 'sports', label: 'Sports & Fitness' },
    { value: 'science', label: 'Science & Tech' },
];

function InfoSection({
    title,
    subtitle,
    children,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <div className={`${card} overflow-hidden`}>
            <div className="px-5 sm:px-8 pt-6 sm:pt-8">
                <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{title}</div>
                {subtitle && <div className="mt-1 text-sm text-grey40 dark:text-nightMuted">{subtitle}</div>}
            </div>
            <div className="px-5 sm:px-8 pt-6 pb-6 sm:pb-8 flex flex-col gap-6">
                {children}
            </div>
        </div>
    );
}

function PillChoice({
    options,
    value,
    onChange,
}: {
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`h-10 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors ${active ? 'bg-primary text-white dark:bg-nightAccent dark:text-nightBg' : 'bg-grey99 text-grey20 ring-1 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/[0.06] dark:hover:bg-white/10'}`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function resizeImageFile(file: File, maxWidth: number, maxHeight: number): Promise<File> {
    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
            const width = Math.round(img.width * scale);
            const height = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(file); return; }
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (!blob) { resolve(file); return; }
                resolve(new File([blob], file.name, { type: blob.type || file.type }));
            }, 'image/jpeg', 0.9);
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
        img.src = objectUrl;
    });
}

function SkeletonCard({ rows = 2, wide = false }: { rows?: number; wide?: boolean }) {
    return (
        <div className={`${card} overflow-hidden px-5 sm:px-8 pt-6 sm:pt-8 pb-6 sm:pb-8`}>
            <div className={`h-6 w-36 ${skel} rounded-full mb-6`}></div>
            <div className={`grid grid-cols-1 ${wide ? 'md:grid-cols-2' : ''} gap-4 md:gap-6`}>
                {Array.from({ length: rows }).map((_, i) => (
                    <div key={i} className="space-y-2">
                        <div className={`h-3 w-24 ${skel} rounded-full`}></div>
                        <div className={`h-12 ${skel} rounded-2xl`}></div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CreateEventSkeleton() {
    return (
        <div className="px-4 xl:px-0 pt-6 sm:pt-10 pb-8 flex flex-col gap-5 max-w-[1200px] mx-auto animate-pulse">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
                <div className="space-y-3">
                    <div className={`h-3 w-24 ${skel} rounded-full`}></div>
                    <div className={`h-12 w-64 ${skel} rounded-2xl`}></div>
                </div>
                <div className={`h-12 w-36 ${skel} rounded-full`}></div>
            </div>
            <SkeletonCard rows={2} wide />
            <SkeletonCard rows={2} wide />
            <SkeletonCard rows={1} />
            <SkeletonCard rows={2} wide />
            <div className={`${card} overflow-hidden px-5 sm:px-8 pt-6 sm:pt-8 pb-6 sm:pb-8`}>
                <div className={`h-6 w-24 ${skel} rounded-full mb-6`}></div>
                <div className={`w-full h-48 ${skel} rounded-[28px]`}></div>
            </div>
            <div className={`${card} overflow-hidden px-5 sm:px-8 pt-6 sm:pt-8 pb-6 sm:pb-8`}>
                <div className={`h-6 w-40 ${skel} rounded-full mb-6`}></div>
                <div className={`h-12 ${skel} rounded-2xl mb-3`}></div>
                <div className={`w-full h-[260px] ${skel} rounded-[24px]`}></div>
            </div>
            <SkeletonCard rows={1} />
        </div>
    );
}

function formatTimeValue(t: any) {
    if (!t || typeof t.hour !== 'number' || typeof t.minute !== 'number') return '';
    return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
}

function parseDraftDateTime(value: any): { date: DateValue | null; time: any } {
    if (!value || typeof value !== 'string') return { date: null, time: null };
    try {
        if (value.includes('T')) {
            const d = new Date(value);
            if (isNaN(d.getTime())) return { date: null, time: null };
            const pad = (n: number) => String(n).padStart(2, '0');
            return {
                date: parseDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`),
                time: parseTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`),
            };
        }
        const [datePart, timePart] = value.trim().split(' ');
        return {
            date: datePart ? parseDate(datePart) : null,
            time: timePart ? parseTime(timePart) : null,
        };
    } catch {
        return { date: null, time: null };
    }
}

function extractAddressFromComponents(components: any[]) {
    const out = { country: '', state: '', city: '', zipcode: '' };
    if (!components || !Array.isArray(components)) return out;
    const find = (t: string) => components.find(c => c.types.includes(t));
    out.country = find('country')?.long_name || '';
    out.state = find('administrative_area_level_1')?.long_name || '';
    out.city = find('locality')?.long_name || '';
    out.zipcode = find('postal_code')?.long_name || '';
    return out;
}

export default function CreateEventNew({ embedded = false }: { embedded?: boolean } = {}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { selectedOrganizerId, setSelectedOrganizerId, isLoading: isLoadingOrganization } = useOrganization();
    const [searchParams] = useSearchParams();
    const draftId = searchParams.get('eventId');

    const [name, setName] = useState('');
    const [handle, setHandle] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');


    const [refundable, setRefundable] = useState(false);
    const [refundDaysBeforeStart, setRefundDaysBeforeStart] = useState<string>('0');
    const [feeMode, setFeeMode] = useState<'added' | 'included'>('included');
    const [taxMode, setTaxMode] = useState<'added' | 'included'>('included');
    const [currency, setCurrency] = useState<EventCurrency>('usd');

    const todayDate = today(getLocalTimeZone());
    const [startDate, setStartDate] = useState<DateValue | null>(todayDate as any);
    const [startTime, setStartTime] = useState<any>(parseTime('18:00'));
    const [endDate, setEndDate] = useState<DateValue | null>(todayDate as any);
    const [endTime, setEndTime] = useState<any>(parseTime('21:00'));

    const [banner, setBanner] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string>('');
    const [bannerError, setBannerError] = useState('');
    const [youtubeVideoLink, setYoutubeVideoLink] = useState('');

    const [address, setAddress] = useState('');
    const [addressData, setAddressData] = useState({ country: '', state: '', city: '', zipcode: '' });
    const [venue, setVenue] = useState('');
    const [venueId, setVenueId] = useState('');
    const [howto, setHowto] = useState('');

    const [isPrivate, setIsPrivate] = useState(0);
    const [ticketInfoMode, setTicketInfoMode] = useState(0);
    const [showOnlyCity, setShowOnlyCity] = useState(0);
    const [commentsDisabled, setCommentsDisabled] = useState(0);
    const [liabilityWaiverEnabled, setLiabilityWaiverEnabled] = useState(false);
    const [liabilityWaiverText, setLiabilityWaiverText] = useState('');

    const [error, setError] = useState('');

    const [handleValidation, setHandleValidation] = useState<{ isValidating: boolean; isAvailable: boolean | null; message: string }>({ isValidating: false, isAvailable: null, message: '' });
    const handleValidationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const defaultLat = 34.0522;
    const defaultLng = -118.2437;
    const [lat, setLat] = useState(defaultLat);
    const [lng, setLng] = useState(defaultLng);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const mapContainerStyle = { width: '100%', height: '260px', borderRadius: '24px' };
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries: ['places'] as any,
    });

    const { data: draftData, isLoading: isLoadingDraft } = useQuery({
        queryKey: ['draftEvent', draftId],
        queryFn: () => fetchDraftById(draftId as string),
        enabled: !!draftId,
    });
    const draftLoadedRef = useRef(false);

    useEffect(() => {
        const draft = draftData?.data;
        if (!draft || draftLoadedRef.current) return;
        draftLoadedRef.current = true;

        if (draft.title) setName(draft.title);
        if (draft.handle) setHandle(draft.handle);
        if (draft.category) setCategory(draft.category);
        if (draft.description) setDescription(draft.description);
        if (draft.refundable !== undefined) setRefundable(draft.refundable === true || draft.refundable === 'true');
        if (draft.refundDaysBeforeStart !== undefined && draft.refundDaysBeforeStart !== null) setRefundDaysBeforeStart(String(draft.refundDaysBeforeStart));
        if (draft.feeMode) setFeeMode(draft.feeMode === 'added' ? 'added' : 'included');
        if (draft.taxMode) setTaxMode(draft.taxMode === 'added' ? 'added' : 'included');
        if (draft.currency) setCurrency(resolveCurrency(draft.currency));

        const time = Array.isArray(draft.times) ? draft.times[0] : null;
        if (time) {
            const start = parseDraftDateTime(time.startDate);
            const end = parseDraftDateTime(time.endDate);
            if (start.date) setStartDate(start.date);
            if (start.time) setStartTime(start.time);
            if (end.date) setEndDate(end.date);
            if (end.time) setEndTime(end.time);
        }

        if (draft.attachments?.banner?.link) setBannerPreview(draft.attachments.banner.link);
        if (draft.youtubeVideoLink) setYoutubeVideoLink(draft.youtubeVideoLink);

        if (draft.address) {
            try {
                const addr = typeof draft.address === 'string' ? JSON.parse(draft.address) : draft.address;
                setAddress(addr.fullAddress || '');
                setAddressData({ country: addr.country || '', state: addr.state || '', city: addr.city || '', zipcode: addr.zipcode || '' });
            } catch {
                // ignore malformed address in draft
            }
        }
        if (draft.venue) setVenue(draft.venue);
        if (draft.venueId) setVenueId(String(draft.venueId?._id || draft.venueId));
        if (draft.howto) setHowto(draft.howto);
        if (draft.lat && !isNaN(parseFloat(draft.lat))) setLat(parseFloat(draft.lat));
        if (draft.lng && !isNaN(parseFloat(draft.lng))) setLng(parseFloat(draft.lng));

        if (draft.isPrivate !== undefined) setIsPrivate(Number(draft.isPrivate) || 0);
        if (draft.ticketInfoMode !== undefined) setTicketInfoMode(Number(draft.ticketInfoMode) || 0);
        if (draft.showOnlyCity !== undefined) setShowOnlyCity(Number(draft.showOnlyCity) || 0);
        if (draft.commentsDisabled !== undefined) setCommentsDisabled(Number(draft.commentsDisabled) || 0);
        if (draft.liabilityWaiverEnabled !== undefined) setLiabilityWaiverEnabled(draft.liabilityWaiverEnabled === true || draft.liabilityWaiverEnabled === 'true');
        if (draft.liabilityWaiverText) setLiabilityWaiverText(String(draft.liabilityWaiverText));

        const orgId = draft.organizerId?._id || draft.organizerId;
        if (orgId) setSelectedOrganizerId(String(orgId));
    }, [draftData, setSelectedOrganizerId]);

    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }
    function onPlaceChanged() {
        if (!autocomplete) return;
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
            const newLat = place.geometry.location.lat();
            const newLng = place.geometry.location.lng();
            setLat(newLat);
            setLng(newLng);
            setAddress(place.formatted_address || '');
            setAddressData(extractAddressFromComponents(place.address_components));
            if (map) map.panTo({ lat: newLat, lng: newLng });
        }
    }
    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }

    async function fetchAddressFromCoords(newLat: number, newLng: number) {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${newLat},${newLng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
            );
            const data = await response.json();
            if (data.results && data.results[0]) {
                const result = data.results[0];
                setAddress(result.formatted_address || '');
                setAddressData(extractAddressFromComponents(result.address_components));
            }
        } catch {
            // ignore reverse-geocode failures, keep the previously typed address
        }
    }

    function onMarkerDragEnd(e: any) {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setLat(newLat);
        setLng(newLng);
        fetchAddressFromCoords(newLat, newLng);
    }

    async function handleBannerUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!(await hasAspectRatio(file))) {
            setBannerError(BANNER_RATIO_ERROR);
            addToast({ title: 'Invalid image', description: BANNER_RATIO_ERROR, color: 'danger' });
            return;
        }
        setBannerError('');
        const resized = await resizeImageFile(file, 1600, 900);
        setBanner(resized);
        setBannerPreview(URL.createObjectURL(resized));
    }

    function selectVenue(v: any) {
        setVenue(v.name || '');
        setVenueId(v._id);
        if (v.address) {
            setAddress(v.address.fullAddress || '');
            setAddressData({ country: v.address.country || '', state: v.address.state || '', city: v.address.city || '', zipcode: v.address.zipcode || '' });
        }
        const vLat = parseFloat(v.lat ?? v.coordinates?.[1]);
        const vLng = parseFloat(v.lng ?? v.coordinates?.[0]);
        if (!isNaN(vLat) && !isNaN(vLng)) {
            setLat(vLat);
            setLng(vLng);
            if (map) map.panTo({ lat: vLat, lng: vLng });
        }
    }


    function handleHandleChange(v: string) {
        const cleaned = v.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setHandle(cleaned);
        if (handleValidationTimeoutRef.current) clearTimeout(handleValidationTimeoutRef.current);
        if (cleaned.length >= 3) {
            setHandleValidation({ isValidating: true, isAvailable: null, message: 'Checking availability...' });
            handleValidationTimeoutRef.current = setTimeout(async () => {
                try {
                    const response = await checkHandleAvailability(cleaned);
                    const avail = response.data.available;
                    setHandleValidation({ isValidating: false, isAvailable: avail, message: avail ? 'Handle is available!' : 'Handle is already taken' });
                } catch {
                    setHandleValidation({ isValidating: false, isAvailable: false, message: 'Error checking availability' });
                }
            }, 500);
        } else {
            setHandleValidation({ isValidating: false, isAvailable: null, message: '' });
        }
    }

    const createMutation = useMutation({
        mutationFn: async () => {
            const formData = new FormData();
            formData.append('title', name);
            formData.append('description', description);
            formData.append('category', category);
            if (handle) formData.append('handle', handle);
            if (banner) formData.append('banner', banner);
            formData.append('times', JSON.stringify([{
                startDate: `${startDate ? startDate.toString() : ''} ${formatTimeValue(startTime)}`.trim(),
                endDate: `${endDate ? endDate.toString() : ''} ${formatTimeValue(endTime)}`.trim(),
            }]));
            formData.append('address', JSON.stringify({ fullAddress: address, ...addressData }));
            formData.append('eventType', 'created');
            formData.append('venue', venue);
            formData.append('howto', howto);
            formData.append('lat', lat.toString());
            formData.append('lng', lng.toString());
            formData.append('organizerId', selectedOrganizerId || '');
            formData.append('isPrivate', isPrivate.toString());
            formData.append('showOnlyCity', showOnlyCity.toString());
            formData.append('commentsDisabled', commentsDisabled.toString());
            formData.append('ticketInfoMode', ticketInfoMode.toString());
            if (youtubeVideoLink) formData.append('youtubeVideoLink', youtubeVideoLink);
            formData.append('tickets', JSON.stringify(Array.isArray(draftData?.data?.tickets) ? draftData.data.tickets : []));

            const res = await createEventWithImages(formData, draftId || undefined);
            const eventId = res?.data?._id || res?.data?.id || res?._id || res?.id;
            if (!eventId) throw new Error('Event created but no id was returned');

            // Fields the create-with-images endpoint doesn't accept are applied right after via the edit endpoint
            await updateEvent(eventId, {
                refundable,
                refundDaysBeforeStart: Number(refundDaysBeforeStart) || 0,
                feeMode,
                taxMode,
                currency,
                liabilityWaiverEnabled,
                liabilityWaiverText: liabilityWaiverEnabled ? liabilityWaiverText : '',
                venueId: venueId || null,
            });

            return eventId;
        },
        onSuccess: (eventId: string) => {
            queryClient.invalidateQueries({ predicate: query => query.queryKey[0] === 'userEvents' });
            navigate(`/organizations/events/${eventId}/Dashboard`);
        },
        onError: (err: any) => {
            setError(err?.message || 'Failed to create event');
        },
    });

    function handleSubmit() {
        setError('');
        if (!name.trim()) { setError('Event name is required'); return; }
        if (!category) { setError('Category is required'); return; }
        if (!banner && !bannerPreview) { setError('Event banner photo is required'); return; }
        if (!startDate || !startTime) { setError('Event start date & time is required'); return; }
        if (!endDate || !endTime) { setError('Event end date & time is required'); return; }
        if (!address.trim()) { setError('Event address is required'); return; }
        if (!venue.trim()) { setError('Venue name is required'); return; }
        createMutation.mutate();
    }

    if (isLoadingOrganization || (draftId && isLoadingDraft)) {
        return <CreateEventSkeleton />;
    }

    return (
        <div className="max-w-[1200px] mx-auto">
            {/* Mobile-only top bar: Back + Save Event */}
            <div className="sm:hidden fixed top-0 left-0 right-0 z-[9999] bg-white/90 border-b border-black/[0.06] dark:bg-nightBg/90 dark:border-white/[0.06]">
                <div className="flex items-center justify-between gap-3 px-4 h-14">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex h-9 items-center gap-1 rounded-full pl-2 pr-3.5 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={createMutation.isPending}
                        className="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary30 disabled:opacity-60 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70"
                    >
                        {createMutation.isPending ? 'Saving...' : 'Save Event'}
                    </button>
                </div>
                <div className="px-4 pb-3 text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Create Event</div>
            </div>

            <div className="px-4 xl:px-0 pt-[112px] sm:pt-6 pb-8 flex flex-col gap-5">
                <div className="hidden sm:flex sm:flex-row sm:items-end justify-between gap-4 sm:sticky sm:top-16 md:top-[76px] z-30 -mx-4 px-4 xl:mx-0 xl:px-0 py-4 bg-white/85 dark:bg-nightBg/85">
                    <div>
                        <div className="text-sm font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">New event</div>
                        <div className="mt-2 text-[clamp(36px,4.5vw,56px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white">Create Event</div>
                    </div>
                    <button
                        onClick={handleSubmit}
                        disabled={createMutation.isPending}
                        className={`${btnAccent} shrink-0`}
                    >
                        {createMutation.isPending ? 'Saving...' : 'Save Event'}
                    </button>
                </div>
                {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600 ring-1 ring-red-100 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20">{error}</div>}

            <InfoSection title="Basic Info">
                <div>
                    <label className={fieldLabel}>Event Name</label>
                    <Input className="w-full" classNames={inputCls} placeholder="Event Name..." value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                        <label className={fieldLabel}>Custom Event URL Handle</label>
                        <Input className="w-full" classNames={inputCls} placeholder="eventup.ai/e/your-handle" value={handle} onChange={e => handleHandleChange(e.target.value)} />
                        {handleValidation.message && (
                            <p className={`text-xs mt-2 ${handleValidation.isAvailable === true ? 'text-green-600 dark:text-green-400' : handleValidation.isAvailable === false ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-nightMuted'}`}>
                                {handleValidation.message}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className={fieldLabel}>Category</label>
                        <Select aria-label="Select"
                            className="w-full"
                            classNames={selectCls}
                            placeholder="Select"
                            selectedKeys={category ? [category] : []}
                            onChange={e => setCategory(e.target.value)}
                        >
                            {CATEGORY_OPTIONS.map(opt => <SelectItem key={opt.value}>{opt.label}</SelectItem>)}
                        </Select>
                    </div>
                </div>
                <div>
                    <label className={fieldLabel}>Description</label>
                    <Textarea className="w-full" classNames={textareaCls} rows={3} placeholder="Description..." value={description} onChange={e => setDescription(e.target.value)} />
                </div>
            </InfoSection>

            <InfoSection title="Refund Policy">
                <div className={toggleRow}>
                    <div>
                        <div className={toggleTitle}>Allow Refunds</div>
                        <div className={toggleDesc}>Refunds will be available until the day you set before the event starts.</div>
                    </div>
                    <Switch isSelected={refundable} onValueChange={setRefundable} className="accent-primary40 shrink-0" classNames={switchCls} />
                </div>
                {refundable && (
                    <div>
                        <label className={fieldLabel}>Days before event start</label>
                        <Input
                            type="number"
                            min="0"
                            max="365"
                            value={refundDaysBeforeStart}
                            onChange={e => setRefundDaysBeforeStart(e.target.value)}
                            classNames={inputCls}
                            placeholder="Days"
                        />
                    </div>
                )}
            </InfoSection>

            <InfoSection title="Currency & Fees">
                <div>
                    <label className={fieldLabel}>Currency</label>
                    <Select
                        aria-label="Currency"
                        classNames={selectCls}
                        selectedKeys={[currency]}
                        disallowEmptySelection
                        onSelectionChange={keys => { const v = Array.from(keys)[0]; if (v) setCurrency(resolveCurrency(String(v))); }}
                    >
                        {CURRENCY_OPTIONS.map(o => <SelectItem key={o.key}>{o.label}</SelectItem>)}
                    </Select>
                    <div className={helpText}>Ticket prices are shown and charged in this currency. It can't be changed once tickets are sold.</div>
                </div>
                <div>
                    <label className={fieldLabel}>Service fee</label>
                    <Select
                        aria-label="Service fee"
                        classNames={selectCls}
                        selectedKeys={[feeMode]}
                        disallowEmptySelection
                        onSelectionChange={keys => { const v = Array.from(keys)[0]; if (v) setFeeMode(String(v) as 'added' | 'included'); }}
                    >
                        <SelectItem key="included">Included in ticket price</SelectItem>
                        <SelectItem key="added">Added on top of ticket price</SelectItem>
                    </Select>
                    <div className={helpText}>Choose whether buyers pay the service fee on top of the ticket price or it is taken out of the ticket price.</div>
                </div>
            </InfoSection>

            <InfoSection title="Date & Time">
                <div>
                    <label className={fieldLabel}>Start <span className="text-red-500">*</span></label>
                    <div className="flex flex-col sm:flex-row gap-3 rounded-2xl bg-grey99/60 p-3 ring-1 ring-black/[0.04] dark:bg-white/[0.03] dark:ring-white/[0.06]">
                        <DatePicker classNames={dateCls} minValue={today(getLocalTimeZone())} value={startDate} onChange={(date) => { setStartDate(date); if (date && endDate && endDate.compare(date) < 0) setEndDate(date); }} />
                        <TimeInput classNames={dateCls} hourCycle={12} value={startTime} onChange={setStartTime} />
                    </div>
                </div>
                <div>
                    <label className={fieldLabel}>End <span className="text-red-500">*</span></label>
                    <div className="flex flex-col sm:flex-row gap-3 rounded-2xl bg-grey99/60 p-3 ring-1 ring-black/[0.04] dark:bg-white/[0.03] dark:ring-white/[0.06]">
                        <DatePicker classNames={dateCls} value={endDate} onChange={setEndDate} minValue={startDate ?? undefined} />
                        <TimeInput classNames={dateCls} hourCycle={12} value={endTime} onChange={setEndTime} />
                    </div>
                </div>
            </InfoSection>

            <InfoSection title="Media">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className={fieldLabel}>Event banner photo <span className="text-red-500">*</span></label>
                        <label className="w-full aspect-video rounded-[28px] border-2 border-dashed border-grey90 bg-grey99 transition-colors hover:border-primary/50 hover:bg-grey95 dark:border-white/15 dark:bg-white/[0.04] dark:hover:border-nightAccent/50 dark:hover:bg-white/[0.06] flex flex-col items-center justify-center cursor-pointer relative overflow-hidden">
                            <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} />
                            {bannerPreview ? (
                                <img src={bannerPreview} alt="Banner preview" className="absolute inset-0 w-full h-full object-cover z-0" />
                            ) : (
                                <>
                                    <span className="text-xs text-grey40 dark:text-nightMuted mb-3">16:9 (e.g. 1920 x 1080)</span>
                                    <span className="inline-flex h-10 items-center rounded-full bg-grey10 px-5 text-sm font-semibold text-white dark:bg-white dark:text-nightBg">Upload</span>
                                </>
                            )}
                        </label>
                        {bannerError && <p className="text-xs text-red-500 mt-2">{bannerError}</p>}
                    </div>
                    <div>
                        <label className={fieldLabel}>Youtube Video Link <span className="font-normal text-grey60 dark:text-nightMuted">(Optional)</span></label>
                        <Input className="w-full" classNames={inputCls} placeholder="Youtube Video Link" value={youtubeVideoLink} onChange={e => setYoutubeVideoLink(e.target.value)} />
                    </div>
                </div>
            </InfoSection>

            <InfoSection title="Location & Venue">
                <div>
                    <label className={fieldLabel}>Event Location <span className="text-red-500">*</span></label>
                    {!isLoaded && (
                        <div className="animate-pulse space-y-2">
                            <div className={`h-12 ${skel} rounded-2xl`}></div>
                            <div className={`w-full h-[260px] ${skel} rounded-[24px]`}></div>
                        </div>
                    )}
                    {isLoaded && (
                        <div>
                            <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                                <Input className="w-full mb-3" classNames={inputCls} placeholder="Search for a place or address" value={address} onChange={e => setAddress(e.target.value)} />
                            </Autocomplete>
                            <GoogleMap mapContainerStyle={mapContainerStyle} zoom={14} onLoad={onMapLoad} center={{ lat, lng }}>
                                <Marker position={{ lat, lng }} draggable onDragEnd={onMarkerDragEnd} />
                            </GoogleMap>
                            <p className={helpText}>Drag the marker to fine-tune the exact location.</p>
                        </div>
                    )}
                </div>
                <div>
                    <label className={fieldLabel}>Venue Name <span className="text-red-500">*</span></label>
                    <VenuePicker
                        name={venue}
                        venueId={venueId || null}
                        onChange={next => {
                            if (next.venue) selectVenue(next.venue);
                            else {
                                setVenue(next.name);
                                setVenueId(next.venueId ?? '');
                            }
                        }}
                    />
                    {venueId && (
                        <p className={helpText}>Address and map were filled from the saved venue.</p>
                    )}
                </div>
                <div>
                    <label className={fieldLabel}>How to Get There</label>
                    <Textarea className="w-full" classNames={textareaCls} rows={2} placeholder="How to Get There..." value={howto} onChange={e => setHowto(e.target.value)} />
                </div>
            </InfoSection>

            <InfoSection title="Settings">
                <div>
                    <label className={fieldLabel}>Event Visibility</label>
                    <PillChoice
                        value={isPrivate === 1 ? 'private' : 'public'}
                        onChange={v => setIsPrivate(v === 'private' ? 1 : 0)}
                        options={[{ value: 'public', label: 'Public' }, { value: 'private', label: 'Private' }]}
                    />
                </div>
                <div>
                    <label className={fieldLabel}>Attendee info per ticket</label>
                    <PillChoice
                        value={String(ticketInfoMode)}
                        onChange={v => setTicketInfoMode(Number(v))}
                        options={[{ value: '0', label: 'Disabled' }, { value: '1', label: 'Optional' }, { value: '2', label: 'Required' }]}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className={toggleRow}>
                        <div>
                            <div className={toggleTitle}>Show only city</div>
                            <div className={toggleDesc}>Show only city name instead of full address to attendees</div>
                        </div>
                        <Switch isSelected={showOnlyCity === 1} onValueChange={c => setShowOnlyCity(c ? 1 : 0)} className="accent-primary40 shrink-0" classNames={switchCls} />
                    </div>
                    <div className={toggleRow}>
                        <div>
                            <div className={toggleTitle}>Disable comments</div>
                            <div className={toggleDesc}>Prevent attendees from posting comments on this event</div>
                        </div>
                        <Switch isSelected={commentsDisabled === 1} onValueChange={c => setCommentsDisabled(c ? 1 : 0)} className="accent-primary40 shrink-0" classNames={switchCls} />
                    </div>
                </div>
                <div className={toggleRow}>
                    <div>
                        <div className={toggleTitle}>Enable liability waiver</div>
                        <div className={toggleDesc}>Attendees must accept the waiver during checkout</div>
                    </div>
                    <Switch
                        isSelected={liabilityWaiverEnabled}
                        onValueChange={(checked) => {
                            setLiabilityWaiverEnabled(!!checked);
                            if (!checked) setLiabilityWaiverText('');
                        }}
                        className="accent-primary40 shrink-0" classNames={switchCls}
                    />
                </div>
                {liabilityWaiverEnabled && (
                    <div>
                        <label className={fieldLabel}>Liability waiver text <span className="text-red-500">*</span></label>
                        <Textarea className="w-full" classNames={textareaCls} rows={4} placeholder="Enter your liability waiver text..." value={liabilityWaiverText} onChange={e => setLiabilityWaiverText(e.target.value)} />
                    </div>
                )}
                </InfoSection>
            </div>
        </div>
    );
}
